import { sectionRegistry } from '../canvas/sectionRegistry';
import { useEffect, useRef } from 'react';
import { type Block } from '@aquilixshop/ui-react';
import { isReadOnlyView } from '../boot/view-handoff';
import { useInertWhenReadOnly } from './readOnlyInert';
import { useBuilderStore } from '../state/builderStore';
import { CountdownDesignPanel } from './CountdownDesignPanel';
import { bt, btp } from '../i18n';
import { blockDisplayName, sectionDisplayName } from './sectionVariantLabel';
import { SectionForm } from '../inspector/SectionForm';
import { BlockInspector } from '../inspector/blocks/BlockInspector';
import {
  ArrowLeftIcon,
  EyeIcon,
  EyeOffIcon,
  MoreIcon,
  TrashIcon,
  blockTypeIcon,
  sectionTypeIcon,
} from '../ui/icons';

/**
 * Right settings panel (build-plan §6 / §7.5) — Shopify theme-editor parity:
 *   - a BREADCRUMB header (Section name, with the section type icon)
 *   - the SCHEMA-DRIVEN auto-form (one closed `FieldControl` switch, unchanged engine)
 *   - "Hide section" + "Remove section" actions at the bottom.
 *
 * Block sections (image-with-text) get a nested block list + per-block form below (inside SectionForm).
 *
 * Focus management (build-plan §7.5): when a NEW section is selected, focus moves to the inspector title
 * so a keyboard/screen-reader user is taken to the controls for what they just selected.
 */
/** Find a block anywhere in a section's nested tree by id (the Shrine recursive block model). */
function findBlockInTree(blocks: Block[], id: string): Block | undefined {
  for (const b of blocks) {
    if (b.id === id) return b;
    if (b.blocks) {
      const found = findBlockInTree(b.blocks, id);
      if (found) return found;
    }
  }
  return undefined;
}

export function Inspector() {
  const selectedSectionId = useBuilderStore((s) => s.selectedSectionId);
  const section = useBuilderStore((s) =>
    s.selectedSectionId ? s.sectionsById[s.selectedSectionId] : undefined,
  );
  const selectedBlockId = useBuilderStore((s) => s.selectedBlockId);
  const selectedCountdown = useBuilderStore((s) => s.selectedCountdown);
  const selectCountdownSurface = useBuilderStore((s) => s.selectCountdownSurface);
  const focusedFieldId = useBuilderStore((s) => s.focusedFieldId);
  const toggleSectionDisabled = useBuilderStore((s) => s.toggleSectionDisabled);
  const removeSection = useBuilderStore((s) => s.removeSection);
  const selectSection = useBuilderStore((s) => s.selectSection);
  const selectBlock = useBuilderStore((s) => s.selectBlock);
  const toggleBlockDisabled = useBuilderStore((s) => s.toggleBlockDisabled);
  const removeBlock = useBuilderStore((s) => s.removeBlock);
  // Superadmin consultation: the panel still READS (that is the point of a consultation), but its two
  // write actions are greyed like their rail twins (founder 25/10/2026).
  const readOnly = isReadOnlyView();

  const def = section ? sectionRegistry[section.type] : undefined;
  // Variant-aware: a `media-overlay` testimonials panel is headed "Video testimonials", not the
  // generic registry label that describes the quote-card anatomy instead.
  const name = section ? sectionDisplayName(section) : null;
  const hidden = !!section?.disabled;

  // A block is "open" when one is selected AND it resolves ANYWHERE in the section's tree — the Shrine
  // model nests blocks (Details → title/price/…), so the lookup is recursive, not a flat find.
  const selectedBlock =
    section && selectedBlockId ? findBlockInTree(section.blocks, selectedBlockId) : undefined;

  const titleRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Consultation: the fields stay READABLE and the panel stays scrollable, but nothing in it can be
  // typed, picked or dragged (see `useInertWhenReadOnly`).
  useInertWhenReadOnly(bodyRef, [selectedSectionId, selectedBlockId, selectedCountdown]);
  // Move focus to the inspector title when the SELECTED section OR block changes (not on every field
  // edit). We only steal focus if focus isn't already inside a text field/menu (don't yank a typing seller).
  useEffect(() => {
    if (!selectedSectionId) return;
    const active = document.activeElement as HTMLElement | null;
    const tag = active?.tagName.toLowerCase();
    const typing = tag === 'input' || tag === 'textarea' || active?.isContentEditable;
    if (typing) return;
    titleRef.current?.focus();
  }, [selectedSectionId, selectedBlockId]);

  // PART-LEVEL selection, inspector half (see `focusField` in the store): when the seller clicks a field
  // hotspot in the canvas, scroll THIS field's row into view, flash it, and move focus into its control —
  // so the drilled title/button opens exactly where its setting lives. Runs after the title-focus effect
  // above (defined later → its focus() wins), so the control — not the panel title — ends up focused.
  useEffect(() => {
    if (!focusedFieldId) return;
    const root = bodyRef.current;
    if (!root) return;
    const key =
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(focusedFieldId) : focusedFieldId;
    const row = root.querySelector<HTMLElement>(`[data-field-key="${key}"]`);
    if (!row) return;
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    row.classList.add('bld-field--flash');
    row
      .querySelector<HTMLElement>(
        'input, textarea, select, button, [contenteditable="true"], [tabindex]',
      )
      ?.focus({ preventScroll: true });
    const t = window.setTimeout(() => row.classList.remove('bld-field--flash'), 1400);
    return () => window.clearTimeout(t);
  }, [focusedFieldId, selectedSectionId, selectedBlockId]);

  // ── The Countdown APP's surface selected → its DESIGN, and only its design (0041 V7). It is not a
  // section, so it has no schema in the registry and no entry in the rail: the panel is keyed on the
  // campaign, and it says out loud which half of the decision lives here.
  if (selectedCountdown) {
    return (
      <aside
        className="bld-inspector"
        aria-label={bt('chrome.insp.countdown_aria', 'Countdown design')}
        aria-labelledby="bld-insp-title"
      >
        <div className="bld-inspector__head">
          <h2
            id="bld-insp-title"
            className="bld-inspector__head-title"
            tabIndex={-1}
            ref={titleRef}
          >
            {bt('chrome.insp.countdown_title', 'Countdown')}
          </h2>
          <div className="bld-inspector__head-actions">
            <button
              type="button"
              className="bld-inspector__head-btn"
              onClick={() => selectCountdownSurface(null)}
              aria-label={bt('chrome.insp.close', 'Close')}
              title={bt('chrome.insp.close', 'Close')}
            >
              ×
            </button>
          </div>
        </div>
        <div className="bld-inspector__body" ref={bodyRef}>
          <CountdownDesignPanel
            campaignId={selectedCountdown.campaignId}
            surface={selectedCountdown.surface}
          />
        </div>
      </aside>
    );
  }

  // ── BLOCK selected → show ONLY the block's fields at the TOP, with a "← section" breadcrumb. This is
  // the fix for "the inspector starts halfway": the block's settings are no longer buried under the
  // section's own settings — selecting a block routes straight to the block panel. ──
  if (selectedSectionId && section && def && selectedBlock) {
    const bschema = def.schema.blocks?.find((b) => b.type === selectedBlock.type);
    const blockName = bschema
      ? blockDisplayName(section.type, selectedBlock.type, bschema.name)
      : selectedBlock.type;
    const blockHidden = !!selectedBlock.disabled;
    return (
      <aside
        className="bld-inspector"
        aria-label={bt('chrome.insp.block_aria', 'Block settings')}
        aria-labelledby="bld-insp-title"
      >
        <button
          type="button"
          className="bld-inspector__crumb"
          onClick={() => selectBlock(null)}
          title={btp('chrome.insp.back_to', 'Back to {name}', { name: name ?? '' })}
        >
          <span className="bld-inspector__crumb-icon dir-flip" aria-hidden="true">
            <ArrowLeftIcon size={16} />
          </span>
          {name}
        </button>
        <div className="bld-inspector__head">
          <span className="bld-inspector__head-icon" aria-hidden="true">
            {blockTypeIcon(selectedBlock.type, 16)}
          </span>
          <h2
            id="bld-insp-title"
            className="bld-inspector__head-title"
            tabIndex={-1}
            ref={titleRef}
          >
            {blockName}
          </h2>
          <div className="bld-inspector__head-actions">
            <button
              type="button"
              className="bld-inspector__head-btn"
              aria-label={btp('chrome.insp.options_aria', '{name} options', { name: blockName })}
              aria-haspopup="menu"
              title={bt('chrome.top.more', 'More options')}
            >
              <MoreIcon />
            </button>
            <button
              type="button"
              className="bld-inspector__head-btn"
              aria-label={bt('chrome.insp.close_block_aria', 'Close block settings')}
              title={bt('chrome.common.close', 'Close')}
              onClick={() => selectBlock(null)}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        </div>
        <div className="bld-inspector__body" ref={bodyRef}>
          {/* key by block id: switching between two blocks (esp. same type) MUST remount the form so
              stateful controls — the Tiptap richtext editor, whose `content` is read only at init —
              re-initialise from the newly selected block instead of showing the previous block's text. */}
          <BlockInspector
            key={selectedBlock.id}
            section={section}
            schema={def.schema}
            block={selectedBlock}
            hideTitle
          />
        </div>
        {/* Static children (Gallery/Details) are theme-fixed — no hide/remove. */}
        {!selectedBlock.static && (
          <div className="bld-inspector__actions">
            <button
              type="button"
              className="bld-inspector__action"
              aria-pressed={blockHidden}
              disabled={readOnly}
              title={readOnly ? bt('chrome.view.readonly', 'Read-only consultation') : undefined}
              onClick={() => toggleBlockDisabled(section.id, selectedBlock.id)}
            >
              <span className="bld-inspector__action-icon" aria-hidden="true">
                {blockHidden ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
              </span>
              {blockHidden
                ? bt('chrome.sec.show_block', 'Show block')
                : bt('chrome.sec.hide_block', 'Hide block')}
            </button>
            <button
              type="button"
              className="bld-inspector__action bld-inspector__action--danger bld-inspector__action--center"
              disabled={readOnly}
              title={readOnly ? bt('chrome.view.readonly', 'Read-only consultation') : undefined}
              onClick={() => removeBlock(section.id, selectedBlock.id)}
            >
              <span className="bld-inspector__action-icon" aria-hidden="true">
                <TrashIcon size={16} />
              </span>
              {bt('chrome.sec.remove_block', 'Remove block')}
            </button>
          </div>
        )}
      </aside>
    );
  }

  return (
    <aside
      className="bld-inspector"
      aria-label={bt('chrome.insp.aria', 'Section settings')}
      aria-labelledby="bld-insp-title"
    >
      {selectedSectionId && section ? (
        <>
          <div className="bld-inspector__head">
            <span className="bld-inspector__head-icon" aria-hidden="true">
              {sectionTypeIcon(def?.meta.icon, 16)}
            </span>
            <h2
              id="bld-insp-title"
              className="bld-inspector__head-title"
              tabIndex={-1}
              ref={titleRef}
            >
              {name}
            </h2>
            <div className="bld-inspector__head-actions">
              <button
                type="button"
                className="bld-inspector__head-btn"
                aria-label={btp('chrome.insp.options_aria', '{name} options', { name: name ?? '' })}
                aria-haspopup="menu"
                title={bt('chrome.top.more', 'More options')}
              >
                <MoreIcon />
              </button>
              <button
                type="button"
                className="bld-inspector__head-btn"
                aria-label={bt('chrome.insp.close_section_aria', 'Close section settings')}
                title={bt('chrome.common.close', 'Close')}
                onClick={() => selectSection(null)}
              >
                <span aria-hidden="true">✕</span>
              </button>
            </div>
          </div>
          <div className="bld-inspector__body" ref={bodyRef}>
            {/* key by section id: same remount reason as BlockInspector — a section's own richtext
                fields (heading/subheading) must reset when switching sections. */}
            <SectionForm key={section.id} section={section} />
          </div>
          <div className="bld-inspector__actions">
            <button
              type="button"
              className="bld-inspector__action"
              aria-pressed={hidden}
              disabled={readOnly}
              title={readOnly ? bt('chrome.view.readonly', 'Read-only consultation') : undefined}
              onClick={() => toggleSectionDisabled(section.id)}
            >
              <span className="bld-inspector__action-icon" aria-hidden="true">
                {hidden ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
              </span>
              {hidden
                ? bt('chrome.sec.show', 'Show section')
                : bt('chrome.sec.hide', 'Hide section')}
            </button>
            <button
              type="button"
              className="bld-inspector__action bld-inspector__action--danger bld-inspector__action--center"
              disabled={readOnly}
              title={readOnly ? bt('chrome.view.readonly', 'Read-only consultation') : undefined}
              onClick={() => removeSection(section.id)}
            >
              <span className="bld-inspector__action-icon" aria-hidden="true">
                <TrashIcon size={16} />
              </span>
              {bt('chrome.sec.remove', 'Remove section')}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="bld-inspector__head">
            <h2 className="bld-inspector__title" id="bld-insp-title">
              {bt('chrome.insp.empty_title', 'No section selected')}
            </h2>
          </div>
          <div className="bld-inspector__empty">
            {bt(
              'chrome.insp.empty_hint',
              'Select a section in the canvas or the rail to edit its settings.',
            )}
          </div>
        </>
      )}
    </aside>
  );
}
